import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    role: 'super_admin' | 'studio_admin' | 'studio_staff' | 'model';
    tenantId: string | null;
    modelProfileId: string | null;
  };
}

export default function StudioLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUserDirectly } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post<LoginResponse>('/auth/login', { email, password });
      const { token, user } = response.data;

      // Only allow studio roles
      if (!['studio_admin', 'studio_staff'].includes(user.role)) {
        setError('Access denied. This login is for studio administrators only.');
        return;
      }

      // Store token and user info
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Update auth context
      setUserDirectly(user);

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page studio-auth">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-logo">MediaRite</h1>
          <p className="auth-subtitle">Studio Management Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Studio Sign In</h2>
          <p className="form-note">Access your studio's compliance dashboard</p>

          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="Enter your studio email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
              minLength={8}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <p className="auth-footer">
            Have an invite code? <Link to="/studioregister">Register your studio</Link>
          </p>

          <p className="auth-note">
            Studio accounts are created by system administrators.
            <br />
            Contact your administrator if you need access.
          </p>
        </form>
      </div>
    </div>
  );
}
